import React, { useEffect, useState, useRef } from "react";
import "./styles.css"; 
import Box from "../Box"; 
import PageTitle from "../PageTitle";
import Spacer from "../Spacer";  

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

interface PartnerMatchedPageProps {
  onGameStart: () => void;
  prolificId: string;
}

const PartnerMatchedPage: React.FC<PartnerMatchedPageProps> = ({ onGameStart, prolificId }) => {
  const [countdown, setCountdown] = useState<number>(5);
  const [countdownEndTime, setCountdownEndTime] = useState<number | null>(null);
  const [tutorialStartTime, setTutorialStartTime] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const dingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasTransitionedRef = useRef<boolean>(false);

  // Play ding sound function
  const playDing = () => {
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // High pitch ding
      oscillator.type = 'sine';
      
      // Maximum volume (1.0 = 100% volume)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(1.0, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      console.warn('PartnerMatchedPage: Could not play ding sound:', error);
    }
  };

  // Initialize synchronized timing from backend
  useEffect(() => {
    const initMatched = async () => {
      try {
        console.log('[PartnerMatchedPage] Initializing synchronized countdown...');
        const res = await fetch(`${API_BASE_URL}/partnerMatched/init`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': prolificId,
          },
        });
        
        if (res.ok) {
          const data = await res.json();
          console.log('[PartnerMatchedPage] Synchronized timing received:', data);
          setCountdownEndTime(data.countdownEndTime);
          setTutorialStartTime(data.tutorialStartTime);
          setIsInitialized(true);
          const remaining = Math.max(0, Math.ceil((data.countdownEndTime - Date.now()) / 1000));
          setCountdown(remaining);
        } else {
          console.warn('[PartnerMatchedPage] Failed to get synchronized timing, using fallback');
          const now = Date.now();
          setCountdownEndTime(now + 5000);
          setTutorialStartTime(now + 5000);
          setIsInitialized(true);
        }
      } catch (err) {
        console.error('[PartnerMatchedPage] Error initializing:', err);
        const now = Date.now();
        setCountdownEndTime(now + 5000);
        setTutorialStartTime(now + 5000);
        setIsInitialized(true);
      }
    };
    
    initMatched();
  }, [prolificId]);

  // Synchronized countdown timer
  useEffect(() => {
    if (!countdownEndTime || !isInitialized) return;

    const updateCountdown = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((countdownEndTime - now) / 1000));
      
      if (remaining !== countdown) {
        setCountdown(remaining);
      }

      // When countdown ends, transition to tutorial (synchronized)
      if (now >= countdownEndTime && !hasTransitionedRef.current) {
        hasTransitionedRef.current = true;
        console.log('[PartnerMatchedPage] Countdown expired - transitioning to tutorial round');
        if (dingIntervalRef.current) {
          clearInterval(dingIntervalRef.current);
          dingIntervalRef.current = null;
        }
        onGameStart();
        return;
      }

      timerRef.current = setTimeout(updateCountdown, 100); // Check every 100ms for accuracy
    };

    timerRef.current = setTimeout(updateCountdown, 100);

    // Play dings continuously if tab is not visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden - play dings continuously every 500ms
        if (!dingIntervalRef.current) {
          playDing(); // Play immediately
          dingIntervalRef.current = setInterval(() => {
            playDing();
          }, 500); // Play every 500ms
        }
      } else {
        // Tab is visible - stop continuous dings
        if (dingIntervalRef.current) {
          clearInterval(dingIntervalRef.current);
          dingIntervalRef.current = null;
        }
      }
    };

    // Check initial visibility state
    if (document.hidden) {
      playDing();
      dingIntervalRef.current = setInterval(() => {
        playDing();
      }, 500);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (dingIntervalRef.current) clearInterval(dingIntervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [countdownEndTime, isInitialized, onGameStart]);

  return (
    <>
      <div className="container"> 
        <PageTitle title="Partner Matched!" />
        <Spacer />
        <Box>
          <>
            <span className="text_content">
              You have been matched with a partner to start the game! <br /><br />
              The game starts in a couple of seconds. Be ready to start the game. <br /><br />
              🎮 Game starting in {countdown} seconds...
            </span>
            <Spacer />
            <div className="countdown-container">
              <div className="countdown-circle">
                <span className="countdown-number">{countdown}</span>
              </div>
            </div>
          </>
        </Box>
      </div>
    </>
  );
};

export default PartnerMatchedPage; 